import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, TrendingDown } from "lucide-react";

interface PlanTiersProps {
  onOpenModal: () => void;
}

const plans = [
  {
    name: "Starter",
    price: "$74",
    priceNum: 74,
    brandedPrice: 550,
    annualCost: "$888",
    brandedComparison: "vs $550/mo branded",
    description: "Entry-level GLP-1 program",
    popular: false,
    features: [
      "Monthly telehealth consultations",
      "Customized weight loss plans",
      "Medication shipped direct",
      "Employer digital portal access",
      "Community membership & support",
    ],
  },
  {
    name: "Semaglutide",
    price: "$149",
    priceNum: 149,
    brandedPrice: 550,
    annualCost: "$1,788",
    brandedComparison: "vs $550/mo branded",
    description: "Premium Semaglutide program",
    popular: true,
    features: [
      "Monthly telehealth consultations",
      "Customized weight loss plans",
      "Medication shipped direct",
      "Employer digital portal access",
      "Community membership & support",
    ],
  },
  {
    name: "Tirzepatide",
    price: "$199",
    priceNum: 199,
    brandedPrice: 800,
    annualCost: "$2,388",
    brandedComparison: "vs $800/mo branded",
    description: "Advanced dual-action GLP-1",
    popular: false,
    features: [
      "Monthly telehealth consultations",
      "Customized weight loss plans",
      "Medication shipped direct",
      "Employer digital portal access",
      "Community membership & support",
    ],
  },
];

const PlanTiers = ({ onOpenModal }: PlanTiersProps) => {
  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#ffdb4d]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-2.5 rounded-full mb-6 shadow-lg shadow-green-500/25">
            <TrendingDown className="w-5 h-5" />
            <span className="text-sm font-bold tracking-wide">Up to 87% Savings vs. Branded</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0D1B2A] mb-6 tracking-tight">
            Invest in Your Team's Peak Performance
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-4 leading-relaxed">
            When employees look and feel their best, they perform at their best. Healthier teams drive higher productivity, lower absenteeism, and stronger business outcomes.
          </p>
          <div className="inline-flex items-center gap-3 bg-[#0D1B2A] text-white px-6 py-3 rounded-xl shadow-xl">
            <span className="text-base font-medium">The ROI is massive —</span>
            <span className="text-[#ffdb4d] font-bold text-lg">$3-$6 return for every $1 invested</span>
          </div>
        </div>

        {/* GLP-1 Category Header */}
        <div className="text-center mb-8">
          <span className="inline-block bg-[#0D1B2A] text-white text-sm font-bold uppercase tracking-widest px-6 py-2 rounded-full">
            GLP-1 Weight Loss Plans
          </span>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const savingsPercent = Math.round(((plan.brandedPrice - plan.priceNum) / plan.brandedPrice) * 100);
            return (
            <Card 
              key={index} 
              className={`relative hover:shadow-xl transition-all duration-300 hover:-translate-y-2 ${
                plan.popular 
                  ? "border-2 border-[#0D1B2A] shadow-lg" 
                  : "border border-gray-200"
              }`}
            >
              {/* Savings Badge */}
              <div className="absolute -top-3 right-4">
                <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                  <TrendingDown className="w-3 h-3" />
                  Save {savingsPercent}%
                </span>
              </div>
              {plan.popular && (
                <div className="absolute -top-3 left-4">
                  <span className="bg-[#ffdb4d] text-[#0D1B2A] text-xs font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              <CardHeader className="text-center pb-2 pt-6">
                <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
                <p className="text-gray-600">{plan.description}</p>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1 flex-wrap">
                    <span className="text-4xl font-bold text-[#0D1B2A]">{plan.price}</span>
                    <span className="text-gray-600">/employee/month</span>
                    <span className="text-sm text-green-600 font-medium ml-1">({plan.brandedComparison})</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Annual cost: {plan.annualCost}/employee
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-[#0D1B2A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={onOpenModal}
                  className={`w-full ${
                    plan.popular 
                      ? "bg-[#ffdb4d] text-[#0D1B2A] hover:bg-[#ffdb4d]/90" 
                      : "bg-[#0D1B2A] hover:bg-[#0D1B2A]/90"
                  }`}
                >
                  Select {plan.name}
                </Button>
              </CardContent>
            </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PlanTiers;
